import React from "react";
import { ArrowUp, ArrowDown, Copy, Trash2, Table2, Plus, X } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { uid } from "../../lib/contractTemplate";

const IconBtn = ({ onClick, title, children, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`rounded-md p-1.5 transition ${danger ? "text-slate-400 hover:bg-red-50 hover:text-red-500" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
  >
    {children}
  </button>
);

const TableEditor = ({ rows, onChange }) => {
  const setCell = (ri, ci, v) => onChange(rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)));
  return (
    <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2">
      <p className="text-[11px] font-semibold text-slate-500">Tabla (la primera fila va resaltada en azul)</p>
      {rows.map((r, ri) => (
        <div key={ri} className="flex items-start gap-1">
          {r.map((cell, ci) => (
            <Input key={ci} value={cell} onChange={(e) => setCell(ri, ci, e.target.value)} className={`h-8 text-xs ${ci === 2 ? "flex-[2]" : "flex-1"}`} />
          ))}
          <IconBtn danger title="Quitar fila" onClick={() => onChange(rows.filter((_, i) => i !== ri))}>
            <X className="h-3.5 w-3.5" />
          </IconBtn>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, ["", "", ""]])} className="text-xs font-semibold text-blue-600">
        + Agregar fila
      </button>
    </div>
  );
};

// Una cláusula: título, texto, tabla opcional y botones para mover/duplicar/borrar.
const ClauseEditor = ({ cl, i, total, update, move, duplicate, remove }) => (
  <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid={`clause-${i}`}>
    <div className="flex items-center gap-1">
      <Input value={cl.title} onChange={(e) => update({ title: e.target.value })} className="h-9 flex-1 font-semibold" data-testid={`clause-title-${i}`} />
      <IconBtn title="Subir" onClick={() => move(-1)}>{i > 0 && <ArrowUp className="h-4 w-4" />}</IconBtn>
      <IconBtn title="Bajar" onClick={() => move(1)}>{i < total - 1 && <ArrowDown className="h-4 w-4" />}</IconBtn>
      <IconBtn title="Duplicar" onClick={duplicate}><Copy className="h-4 w-4" /></IconBtn>
      <IconBtn danger title="Eliminar" onClick={remove}><Trash2 className="h-4 w-4" /></IconBtn>
    </div>
    <Textarea rows={5} value={cl.body} onChange={(e) => update({ body: e.target.value })} className="text-sm leading-relaxed" data-testid={`clause-body-${i}`} />
    {cl.table ? (
      <>
        <TableEditor rows={cl.table} onChange={(t) => update({ table: t })} />
        <button type="button" onClick={() => update({ table: null })} className="text-xs font-semibold text-red-500">Quitar tabla</button>
      </>
    ) : (
      <button type="button" onClick={() => update({ table: [["Encabezado 1", "Encabezado 2", "Encabezado 3"], ["", "", ""]] })}
        className="flex items-center gap-1 text-xs font-semibold text-blue-600">
        <Table2 className="h-3.5 w-3.5" /> Agregar tabla
      </button>
    )}
  </div>
);

export const ClausesEditor = ({ clauses, onChange }) => {
  const upd = (i, patch) => onChange(clauses.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= clauses.length) return;
    const n = [...clauses];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="space-y-2">
      {clauses.map((cl, i) => (
        <ClauseEditor
          key={cl.id}
          cl={cl}
          i={i}
          total={clauses.length}
          update={(p) => upd(i, p)}
          move={(d) => move(i, d)}
          duplicate={() => onChange([...clauses.slice(0, i + 1), { ...cl, id: uid(), table: cl.table && cl.table.map((r) => [...r]) }, ...clauses.slice(i + 1)])}
          remove={() => window.confirm(`¿Eliminar "${cl.title}"?`) && onChange(clauses.filter((_, j) => j !== i))}
        />
      ))}
      <button
        type="button"
        onClick={() => onChange([...clauses, { id: uid(), title: "NUEVA CLÁUSULA", body: "", table: null }])}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 py-2.5 text-sm font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600"
        data-testid="add-clause"
      >
        <Plus className="h-4 w-4" /> Agregar cláusula
      </button>
    </div>
  );
};
